import type { BaseComponentProps } from '@json-render/react';
import filter from 'lodash/filter';
import find from 'lodash/find';
import forEach from 'lodash/forEach';
import groupBy from 'lodash/groupBy';
import includes from 'lodash/includes';
import isFinite from 'lodash/isFinite';
import isNil from 'lodash/isNil';
import isNumber from 'lodash/isNumber';
import isObject from 'lodash/isObject';
import isString from 'lodash/isString';
import keys from 'lodash/keys';
import map from 'lodash/map';
import sortBy from 'lodash/sortBy';
import React, { useCallback, useMemo, useState } from 'react';
import {
  Area,
  CartesianGrid,
  ComposedChart,
  Legend,
  Line,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import type { z } from 'zod';

import type { FundamentalsSeasonalityChartProps as FundamentalsSeasonalityChartSchema } from './catalog';
import { useDataUrl } from './useDataUrl';

type FundamentalsSeasonalityChartProps = z.infer<typeof FundamentalsSeasonalityChartSchema>;

type Frequency = 'D' | 'W' | 'M';

interface Row {
  date: string;
  value: number;
}

interface ChartPoint {
  x: number;
  // year-keyed values — typed loosely because the keys are dynamic year strings
  [series: string]: number | [number, number] | null | undefined;
}

// Theme-aware tooltip styling — mirrors components.tsx so this chart matches
// the rest of the GenUI catalog.
const TOOLTIP_STYLE: React.CSSProperties = {
  background: 'hsl(var(--popover))',
  color: 'hsl(var(--popover-foreground))',
  border: '1px solid hsl(var(--border))',
  borderRadius: 6,
  fontSize: 12,
  padding: '6px 10px',
  boxShadow: '0 4px 6px -1px rgba(0,0,0,.1), 0 2px 4px -2px rgba(0,0,0,.1)',
};

const AXIS_TICK = { fontSize: 11 };
const AXIS_LINE = { stroke: 'hsl(var(--border))' };
const GRID_PROPS = { horizontal: true, vertical: false, stroke: 'hsl(var(--border))', strokeOpacity: 0.5 } as const;

const MONTH_LABELS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
// Day-of-year of the first day of each month in a non-leap year (1-indexed).
// Used to place month tick marks for daily-frequency data.
const MONTH_START_DOY = [1, 32, 60, 91, 121, 152, 182, 213, 244, 274, 305, 335];

// Series keys for the synthetic stat series — prefixed so they can't collide
// with a literal year key like "2025".
const BAND_KEY = '__band';
const AVG_KEY = '__avg';
const HISTORICAL_WINDOW = 5;

// House seasonality convention (matches the PowerBI dashboards): most recent
// year black, previous year red, the year before green. Years further back
// cycle through catalog chart vars that stay clear of black/red/green. The
// price seasonality chart (seasonality.tsx) pins the same three colors.
const HISTORICAL_COLORS = [
  'hsl(var(--chart-3))',
  'hsl(var(--chart-4))',
  'hsl(var(--chart-5))',
  'hsl(var(--chart-2))',
  'hsl(var(--chart-3) / 0.6)',
  'hsl(var(--chart-4) / 0.6)',
  'hsl(var(--chart-5) / 0.6)',
  'hsl(var(--chart-2) / 0.6)',
];
const CURRENT_YEAR_COLOR = '#000000';
const PRIOR_YEAR_COLOR = '#D32F2F';
const SECOND_PRIOR_YEAR_COLOR = '#2E7D32';
const BAND_COLOR = 'hsl(var(--chart-1))';
const AVG_COLOR = 'hsl(var(--muted-foreground))';

// ---------------------------------------------------------------------------
// Data shaping helpers
// ---------------------------------------------------------------------------

function parseRows (raw: unknown[], dateKey: string, valueKey: string): Row[] {
  const out: Row[] = [];
  forEach(raw, (entry) => {
    if (isNil(entry) || !isObject(entry)) return;
    const r = entry as Record<string, unknown>;
    const rawDate = r[dateKey];
    const rawValue = r[valueKey];
    if (!isString(rawDate)) return;
    const value = Number(rawValue);
    if (!isFinite(value)) return;
    // Cheap ISO-date guard — Date.parse accepts way more than we want here.
    const parsed = new Date(rawDate);
    if (isNaN(parsed.getTime())) return;
    out.push({ date: rawDate, value });
  });
  return sortBy(out, (r) => r.date);
}

function inferFrequency (rows: Row[]): Frequency {
  if (rows.length < 2) return 'D';
  const diffs: number[] = [];
  for (let i = 1; i < rows.length; i += 1) {
    const a = new Date(rows[i - 1].date).getTime();
    const b = new Date(rows[i].date).getTime();
    if (b > a) diffs.push((b - a) / (1000 * 60 * 60 * 24));
  }
  if (diffs.length === 0) return 'D';
  const sorted = [...diffs].sort((a, b) => a - b);
  const median = sorted[Math.floor(sorted.length / 2)];
  if (median <= 2) return 'D';
  if (median <= 15) return 'W';
  return 'M';
}

function isLeapYear (year: number): boolean {
  return (year % 4 === 0 && year % 100 !== 0) || (year % 400 === 0);
}

function dayOfYear (date: Date): number {
  const start = Date.UTC(date.getUTCFullYear(), 0, 1);
  const ts = Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate());
  const doy = Math.floor((ts - start) / 86400000) + 1;
  // Normalize leap years onto the same 1..365 axis used by non-leap years so
  // the same calendar date stacks on the same x slot across years:
  //   - Feb 29 → snap to slot 59 (Feb 28); last-write-wins if both rows exist
  //   - Mar 1 onward in a leap year → shift left by one
  if (isLeapYear(date.getUTCFullYear())) {
    if (date.getUTCMonth() === 1 && date.getUTCDate() === 29) return 59;
    if (date.getUTCMonth() > 1) return doy - 1;
  }
  return doy;
}

function isoWeek (date: Date): number {
  // ISO 8601 week number — Thursday in current week decides the year.
  const d = new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate()));
  const dayNum = d.getUTCDay() || 7;
  d.setUTCDate(d.getUTCDate() + 4 - dayNum);
  const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
  return Math.ceil((((d.getTime() - yearStart.getTime()) / 86400000) + 1) / 7);
}

function xIndexFor (date: Date, frequency: Frequency): number {
  if (frequency === 'M') return date.getUTCMonth() + 1;
  if (frequency === 'W') return isoWeek(date);
  return dayOfYear(date);
}

function maxXFor (frequency: Frequency): number {
  if (frequency === 'M') return 12;
  if (frequency === 'W') return 53;
  return 365;
}

// Inverse of dayOfYear on the non-leap-year axis we project all years onto.
// Used by the daily tooltip to render "Mar 15" for an arbitrary doy hover.
const NON_LEAP_DAYS_IN_MONTH = [31, 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];
function doyToMonthDay (doy: number): { month: number; day: number } {
  const clamped = Math.max(1, Math.min(365, Math.round(doy)));
  let remaining = clamped;
  for (let m = 0; m < 12; m += 1) {
    const daysInMonth = NON_LEAP_DAYS_IN_MONTH[m];
    if (remaining <= daysInMonth) return { month: m + 1, day: remaining };
    remaining -= daysInMonth;
  }
  return { month: 12, day: 31 };
}

// ---------------------------------------------------------------------------
// Build chart-ready rows
// ---------------------------------------------------------------------------

interface ChartShape {
  data: ChartPoint[];
  years: number[];
  historicalYears: number[]; // years actually used in the band/avg
  frequency: Frequency;
  currentYear: number;
  priorYear: number | null;
}

function buildChart (
  rows: Row[],
  frequency: Frequency,
  currentYearOverride?: number,
): ChartShape {
  const allYears = sortBy(
    Array.from(new Set(map(rows, (r) => new Date(r.date).getUTCFullYear()))),
  );
  if (allYears.length === 0) {
    return { data: [], years: [], historicalYears: [], frequency, currentYear: new Date().getUTCFullYear(), priorYear: null };
  }
  const currentYear = currentYearOverride ?? allYears[allYears.length - 1];
  const priorYear = allYears[allYears.length - 1] === currentYear
    ? (allYears[allYears.length - 2] ?? null)
    : (allYears[allYears.length - 1] ?? null);
  const historicalYears = filter(
    allYears,
    (y) => y < currentYear && y >= currentYear - HISTORICAL_WINDOW,
  );

  // Group rows by year, then by x-index. Multiple rows landing on the same
  // (year, xIndex) — possible for daily data where a Sunday and Monday would
  // share an ISO week — get last-write-wins after sorting by date.
  const byYear = groupBy(rows, (r) => new Date(r.date).getUTCFullYear());
  const valueAt: Record<number, Map<number, number>> = {};
  forEach(byYear, (yearRows, yearStr) => {
    const year = Number(yearStr);
    const m = new Map<number, number>();
    forEach(yearRows, (r) => {
      const xi = xIndexFor(new Date(r.date), frequency);
      m.set(xi, r.value);
    });
    valueAt[year] = m;
  });

  const maxX = maxXFor(frequency);
  const data: ChartPoint[] = [];
  for (let x = 1; x <= maxX; x += 1) {
    const point: ChartPoint = { x };
    let bandLo = Infinity;
    let bandHi = -Infinity;
    let avgSum = 0;
    let avgCount = 0;
    forEach(allYears, (year) => {
      const v = valueAt[year]?.get(x);
      if (!isNil(v) && isFinite(v)) {
        point[String(year)] = v;
        if (year < currentYear && year >= currentYear - HISTORICAL_WINDOW) {
          if (v < bandLo) bandLo = v;
          if (v > bandHi) bandHi = v;
          avgSum += v;
          avgCount += 1;
        }
      }
    });
    // Only emit the band/avg when a full HISTORICAL_WINDOW of years contributed
    // at this x-position. Partial coverage produces a misleading "min/max" that
    // shrinks at the trailing edge of the year, so we drop those points instead
    // of drawing an artificially narrow band.
    if (avgCount >= HISTORICAL_WINDOW) {
      point[BAND_KEY] = [bandLo, bandHi];
      point[AVG_KEY] = avgSum / avgCount;
    }
    data.push(point);
  }

  return {
    data,
    years: sortBy(allYears, (y) => -y), // descending so the legend reads new → old
    historicalYears,
    frequency,
    currentYear,
    priorYear,
  };
}

// ---------------------------------------------------------------------------
// X-axis tick math
// ---------------------------------------------------------------------------

function tickConfig (frequency: Frequency): { ticks: number[]; format: (v: number) => string } {
  if (frequency === 'M') {
    return {
      ticks: [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12],
      format: (v) => MONTH_LABELS[v - 1] ?? '',
    };
  }
  if (frequency === 'W') {
    return {
      ticks: [1, 5, 9, 13, 17, 21, 25, 29, 33, 37, 41, 45, 49, 53],
      format: (v) => `Wk ${v}`,
    };
  }
  return {
    ticks: MONTH_START_DOY,
    format: (v) => {
      const idx = MONTH_START_DOY.indexOf(v);
      return idx >= 0 ? MONTH_LABELS[idx] : '';
    },
  };
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

// Tooltip entries — `value` may be a `[lo, hi]` array because the band Area
// series resolves to a 2-tuple per point.
interface TooltipPayloadEntry {
  dataKey?: string | number;
  name?: string | number;
  color?: string;
  value?: number | [number, number];
}

// Legend entries — Recharts' Legend onClick/formatter pass a `Payload`-shaped
// object whose `dataKey` is `DataKey<any>` (string | number | function). Mirror
// that here so our handlers are structurally assignable to Recharts' types;
// keyFromEntry below only treats primitive keys as identifiers.
interface LegendEntry {
  dataKey?: string | number | ((obj: unknown) => unknown);
  value?: unknown;
}
const keyFromEntry = (entry: LegendEntry): string => {
  const { dataKey, value } = entry;
  if (isString(dataKey) || isNumber(dataKey)) return String(dataKey);
  if (isString(value) || isNumber(value)) return String(value);
  return '';
};

const FundamentalsSeasonalityTooltip = ({
  active,
  payload,
  label,
  frequency,
}: {
  active?: boolean;
  payload?: TooltipPayloadEntry[];
  label?: number;
  frequency: Frequency;
}) => {
  if (!active || !payload?.length || isNil(label)) return null;
  const labelText = (() => {
    if (frequency === 'W') return `Week ${label}`;
    if (frequency === 'D') {
      // Daily hovers — show "Mon Day" (e.g. "Mar 15") for the day-of-year the
      // cursor is over, derived against a non-leap-year calendar so it lines
      // up with the tick anchors regardless of the source year.
      const { month, day } = doyToMonthDay(label);
      return `${MONTH_LABELS[month - 1]} ${day}`;
    }
    return MONTH_LABELS[label - 1] ?? `Month ${label}`;
  })();
  return (
    <div style={TOOLTIP_STYLE}>
      <div style={{ fontWeight: 600, marginBottom: 4 }}>{labelText}</div>
      {map(payload, (entry, i) => {
        const key = String(entry.dataKey ?? entry.name ?? '');
        if (key === BAND_KEY || isNil(entry.value)) return null;
        const display = key === AVG_KEY
          ? `${HISTORICAL_WINDOW}yr avg`
          : key;
        const v = isNumber(entry.value) ? entry.value : entry.value[1];
        return (
          <div key={`${key}-${i}`} style={{ display: 'flex', alignItems: 'center', gap: 6, margin: '2px 0' }}>
            <span
              style={{
                display: 'inline-block',
                width: 8,
                height: 8,
                borderRadius: 2,
                background: entry.color,
              }}
            />
            <span>{display}: <strong>{Number.isInteger(v) ? v : v.toFixed(2)}</strong></span>
          </div>
        );
      })}
    </div>
  );
};

export const FundamentalsSeasonalityChart = ({
  props,
}: BaseComponentProps<FundamentalsSeasonalityChartProps>) => {
  const rawData = useDataUrl(props.dataUrl, props.data) as unknown[];
  const dateKey = props.dateKey ?? 'date';
  const valueKey = props.valueKey ?? 'value';
  const height = props.height ?? 360;

  const rows = useMemo(() => parseRows(rawData, dateKey, valueKey), [rawData, dateKey, valueKey]);

  const frequency: Frequency = useMemo(() => {
    const claimed = props.frequency;
    if (claimed === 'D' || claimed === 'W' || claimed === 'M') return claimed;
    return inferFrequency(rows);
  }, [props.frequency, rows]);

  const { data, years, currentYear, priorYear } = useMemo(
    () => buildChart(rows, frequency, props.currentYear),
    [rows, frequency, props.currentYear],
  );

  // Third pinned year for the color convention and default visibility — the
  // most recent year that isn't the current or prior year (`years` is sorted
  // descending by buildChart).
  const secondPriorYear = useMemo(
    () => find(years, (y) => y !== currentYear && y !== priorYear) ?? null,
    [years, currentYear, priorYear],
  );

  // Initial visibility: the three most recent years shown, all other years
  // hidden (or whatever `props.visibleYears` overrides to). Stat overlays
  // (band + avg) start visible. Stored as a "hidden keys" map so adding a new
  // year via streaming data leaves existing toggles alone.
  const [hidden, setHidden] = useState<Record<string, boolean>>({});
  const visibleYearsProp = props.visibleYears;
  const isHidden = useCallback(
    (key: string): boolean => {
      if (key in hidden) return hidden[key];
      const yearNum = Number(key);
      if (!Number.isNaN(yearNum) && yearNum > 0) {
        if (visibleYearsProp) return !includes(visibleYearsProp, yearNum);
        return yearNum !== currentYear && yearNum !== priorYear && yearNum !== secondPriorYear;
      }
      return false;
    },
    [hidden, currentYear, priorYear, secondPriorYear, visibleYearsProp],
  );

  const onLegendClick = useCallback((entry: LegendEntry) => {
    const key = keyFromEntry(entry);
    if (!key) return;
    setHidden((prev) => ({ ...prev, [key]: !isHidden(key) }));
  }, [isHidden]);

  const legendFormatter = useCallback(
    (value: string, entry: LegendEntry) => {
      const key = keyFromEntry(entry);
      const dimmed = isHidden(key);
      return (
        <span style={{ opacity: dimmed ? 0.4 : 1, textDecoration: dimmed ? 'line-through' : undefined }}>
          {value}
        </span>
      );
    },
    [isHidden],
  );

  const ticks = tickConfig(frequency);
  const maxX = maxXFor(frequency);
  // Band/avg are only computed at x-positions with full HISTORICAL_WINDOW
  // coverage (see buildChart), so the labels are fixed regardless of how many
  // historical years are present.
  const bandLabel = `${HISTORICAL_WINDOW}yr range`;
  const avgLabel = `${HISTORICAL_WINDOW}yr avg`;

  if (rows.length === 0) {
    return (
      <div className="flex flex-col gap-1">
        {props.title && (
          <div className="text-sm font-semibold text-foreground mb-1">{props.title}</div>
        )}
        <div
          className="flex items-center justify-center text-muted-foreground text-sm"
          style={{ height }}
        >
          No data
        </div>
      </div>
    );
  }

  // Order series so paint order = back-to-front:
  //   band → avg → older years → prior year → current year (on top, bolder)
  const olderYears = filter(years, (y) => y !== currentYear && y !== priorYear);

  // Color lookup by year — keeps the legend swatch in sync with the line.
  // `props.yearColors` overrides individual years; unlisted years keep the
  // default convention (current black, prior red, second-prior green).
  const yearColorsProp = props.yearColors;
  const yearColor = (year: number): string => {
    const override = yearColorsProp?.[String(year)];
    if (override) return override;
    if (year === currentYear) return CURRENT_YEAR_COLOR;
    if (year === priorYear) return PRIOR_YEAR_COLOR;
    if (year === secondPriorYear) return SECOND_PRIOR_YEAR_COLOR;
    const idx = olderYears.indexOf(year);
    // olderYears[0] is secondPriorYear (pinned green above), so start the
    // cycling palette from the next-older year.
    return HISTORICAL_COLORS[Math.max(0, idx - 1) % HISTORICAL_COLORS.length];
  };

  // Build a set of dataKeys that actually have at least one defined value in
  // the chart. Used to drop empty series from the legend — the band/avg are
  // only emitted at x-positions with full HISTORICAL_WINDOW coverage, so a
  // dataset with fewer than 5 historical years yields an empty band/avg series
  // that shouldn't take up legend space.
  const populatedKeys = new Set<string>();
  forEach(data, (point) => {
    forEach(keys(point), (key) => {
      if (key === 'x') return;
      if (!isNil(point[key])) populatedKeys.add(key);
    });
  });

  // Build the legend payload explicitly so the displayed order is years
  // newest → oldest, then 5yr range, then 5yr avg — independent of the
  // paint-order-driven series declaration below.
  const legendPayload = filter(
    [
      ...map(years, (year) => ({
        value: String(year),
        dataKey: String(year),
        type: 'square' as const,
        color: yearColor(year),
      })),
      { value: bandLabel, dataKey: BAND_KEY, type: 'square' as const, color: BAND_COLOR },
      { value: avgLabel, dataKey: AVG_KEY, type: 'square' as const, color: AVG_COLOR },
    ],
    (entry) => populatedKeys.has(entry.dataKey),
  );

  // Bundle into a single object and spread onto <Legend> — TS checks JSX
  // attribute assignment more strictly than spread, and Recharts' Legend
  // prop types don't expose all the variants we need (custom payload + our
  // loose LegendEntry handler signatures). Mirrors the pattern in
  // components.tsx#useLegendToggle.
  const legendProps = {
    verticalAlign: 'bottom' as const,
    align: 'center' as const,
    iconType: 'square' as const,
    iconSize: 12,
    wrapperStyle: {
      fontSize: 12,
      color: 'hsl(var(--muted-foreground))',
      paddingTop: 4,
      cursor: 'pointer',
      userSelect: 'none' as const,
    },
    onClick: onLegendClick,
    formatter: legendFormatter,
    payload: legendPayload,
  };

  return (
    <div className="flex flex-col gap-1">
      {props.title && (
        <div className="text-sm font-semibold text-foreground">{props.title}</div>
      )}
      {props.subtitle && (
        <div className="text-xs text-muted-foreground mb-1">{props.subtitle}</div>
      )}
      <ResponsiveContainer width="100%" height={height}>
        <ComposedChart data={data} margin={{ top: 8, right: 16, bottom: 4, left: 8 }}>
          <CartesianGrid {...GRID_PROPS} />
          <XAxis
            dataKey="x"
            type="number"
            domain={[1, maxX]}
            ticks={ticks.ticks}
            tickFormatter={ticks.format}
            tick={AXIS_TICK}
            tickLine={false}
            axisLine={AXIS_LINE}
            allowDecimals={false}
            interval={0}
            className="fill-muted-foreground"
          />
          <YAxis
            // Recharts defaults the y-axis domain to [0, 'auto'], which wastes
            // vertical space for fundamentals series that sit far from zero —
            // default to fitting the data range instead.
            domain={(props.yDomain as [number | string, number | string] | undefined) ?? ['auto', 'auto']}
            tick={AXIS_TICK}
            tickLine={false}
            axisLine={AXIS_LINE}
            className="fill-muted-foreground"
            label={
              props.yAxisLabel
                ? { value: props.yAxisLabel, angle: -90, position: 'insideLeft', fontSize: 11, offset: 8 }
                : undefined
            }
          />
          <Tooltip
            content={
              <FundamentalsSeasonalityTooltip frequency={frequency} />
            }
          />
          <Legend {...legendProps} />

          {/* Range band — drawn first so lines paint on top. Recharts renders
              `[lo, hi]` arrays as a vertical span when assigned to an Area's
              dataKey. Per-x band/avg points are only emitted when a full
              HISTORICAL_WINDOW of years contributed (see buildChart). We keep
              `connectNulls` so weekend gaps in daily-weekday data bridge
              cleanly — the data-level rule still drops misleading partial
              points before they're rendered. */}
          <Area
            dataKey={BAND_KEY}
            name={bandLabel}
            type="linear"
            stroke="none"
            fill={BAND_COLOR}
            fillOpacity={0.14}
            isAnimationActive={false}
            connectNulls
            hide={isHidden(BAND_KEY)}
            legendType="square"
          />
          <Line
            dataKey={AVG_KEY}
            name={avgLabel}
            type="linear"
            stroke={AVG_COLOR}
            strokeWidth={1.5}
            strokeDasharray="6 3"
            dot={false}
            activeDot={false}
            isAnimationActive={false}
            connectNulls
            hide={isHidden(AVG_KEY)}
          />
          {/* Older historical years — toggleable, only second-prior visible by default */}
          {map(olderYears, (year) => {
            const key = String(year);
            return (
              <Line
                key={key}
                dataKey={key}
                name={key}
                type="linear"
                stroke={yearColor(year)}
                strokeWidth={year === secondPriorYear ? 1.5 : 1.25}
                dot={false}
                activeDot={{ r: 3 }}
                isAnimationActive={false}
                connectNulls
                hide={isHidden(key)}
              />
            );
          })}
          {/* Prior year — thinner than current */}
          {!isNil(priorYear) && (
            <Line
              dataKey={String(priorYear)}
              name={String(priorYear)}
              type="linear"
              stroke={yearColor(priorYear)}
              strokeWidth={1.5}
              dot={false}
              activeDot={{ r: 3 }}
              isAnimationActive={false}
              connectNulls
              hide={isHidden(String(priorYear))}
            />
          )}
          {/* Current year — drawn last so it sits on top, slightly bolder */}
          <Line
            dataKey={String(currentYear)}
            name={String(currentYear)}
            type="linear"
            stroke={yearColor(currentYear)}
            strokeWidth={2.5}
            dot={false}
            activeDot={{ r: 4 }}
            isAnimationActive={false}
            connectNulls
            hide={isHidden(String(currentYear))}
          />
        </ComposedChart>
      </ResponsiveContainer>
    </div>
  );
};
